import api from '../../api/apiClient';
import { logError } from '../../utils/logger';
import type { QuotationPdfFile } from '../admin/admin.api';

/**
 * Double-entry accounting (V29), one set of books per company: /api/accounting/{companyId}/…
 *
 *   books, groups, ledgers      the masters (books' dates are admin only)
 *   vouchers                    register, enter, alter (with version), cancel, history, print
 *   reports/*                   day book, ledger, cash / bank book, trial balance, integrity
 *
 * Every rule (voucher types, Dr = Cr, lock dates, one financial year per report) is checked by
 * the server; the screens mirror the obvious ones so the user is not surprised by a refusal.
 * Amounts arrive as numbers in rupees with two decimals; dates are yyyy-MM-dd.
 */

// ── Shapes ─────────────────────────────────────────────────────────────────

export type Side = 'Dr' | 'Cr';
export type Nature = 'ASSET' | 'LIABILITY' | 'INCOME' | 'EXPENSE';
export type CashBank = 'CASH' | 'BANK';
export type VoucherType = 'PAYMENT' | 'RECEIPT' | 'CONTRA' | 'JOURNAL';
export type VoucherStatus = 'POSTED' | 'CANCELLED';

export interface Balance {
  amount: number;
  side: Side;
}

export interface Books {
  companyId: number;
  companyName: string;
  booksBeginDate: string;
  lockedUntil: string | null;
  hasVouchers: boolean;
}

export interface Group {
  id: number;
  parentId: number | null;
  name: string;
  nature: Nature;
  cashBank: CashBank | null;
  system: boolean;
  sortOrder: number;
  ledgerCount: number;
}

export interface GroupSave {
  parentId: number | null;
  name: string;
  nature?: Nature | null;
  sortOrder?: number | null;
}

export interface Ledger {
  id: number;
  groupId: number;
  groupName: string;
  name: string;
  code: string | null;
  nature: Nature;
  cashBank: CashBank | null;
  openingBalance: number;
  /** Upper case here ("DR" / "CR"), unlike a Balance's side. */
  openingSide: 'DR' | 'CR';
  bankAccountNo: string | null;
  bankIfsc: string | null;
  bankBranch: string | null;
  gstin: string | null;
  pan: string | null;
  address: string | null;
  active: boolean;
  system: boolean;
  currentBalance: Balance;
}

export interface LedgerSave {
  groupId: number;
  name: string;
  code?: string | null;
  openingBalance?: number | null;
  openingSide?: 'DR' | 'CR' | null;
  bankAccountNo?: string | null;
  bankIfsc?: string | null;
  bankBranch?: string | null;
  gstin?: string | null;
  pan?: string | null;
  address?: string | null;
  active?: boolean;
}

export interface LineIn {
  ledgerId: number;
  debit: number | null;
  credit: number | null;
  narration?: string | null;
}

export interface VoucherIn {
  type: VoucherType;
  date: string;
  referenceNo?: string | null;
  referenceDate?: string | null;
  narration?: string | null;
  lines: LineIn[];
  /** Required to alter: the version the screen loaded. */
  version?: number | null;
}

export interface VoucherLine {
  lineNo: number;
  ledgerId: number;
  ledgerName: string;
  groupName: string;
  debit: number | null;
  credit: number | null;
  narration: string | null;
}

export interface Voucher {
  id: number;
  companyId: number;
  type: VoucherType;
  voucherNo: string;
  date: string;
  referenceNo: string | null;
  referenceDate: string | null;
  narration: string | null;
  totalAmount: number;
  status: VoucherStatus;
  cancelReason: string | null;
  version: number;
  createdByName: string | null;
  createdAt: string | null;
  updatedByName: string | null;
  updatedAt: string | null;
  lines: VoucherLine[];
}

export interface VoucherRow {
  id: number;
  type: VoucherType;
  voucherNo: string;
  date: string;
  particulars: string | null;
  narration: string | null;
  totalAmount: number;
  status: VoucherStatus;
}

export interface AuditEntry {
  action: string;
  version: number;
  userName: string | null;
  at: string;
  snapshot: string | null;
}

export interface DayBook {
  from: string;
  to: string;
  vouchers: Voucher[];
  totalDebit: number;
  totalCredit: number;
}

export interface LedgerEntry {
  date: string;
  voucherId: number;
  voucherNo: string;
  voucherType: VoucherType;
  /** The ledgers on the other side of the voucher. */
  particulars: string | null;
  narration: string | null;
  debit: number | null;
  credit: number | null;
  /** Running balance after this entry. */
  balance: Balance;
}

export interface LedgerStatement {
  ledgerId: number;
  ledgerName: string;
  groupName: string;
  nature: Nature;
  from: string;
  to: string;
  opening: Balance;
  entries: LedgerEntry[];
  totalDebit: number;
  totalCredit: number;
  closing: Balance;
}

export interface CashBankBook {
  kind: CashBank;
  from: string;
  to: string;
  ledgers: LedgerStatement[];
  opening: Balance;
  totalDebit: number;
  totalCredit: number;
  closing: Balance;
}

export type TbKind = 'GROUP' | 'LEDGER' | 'PNL' | 'OPENING_DIFF';
export interface TbRow {
  kind: TbKind;
  id: number | null;
  parentId: number | null;
  depth: number;
  name: string;
  nature: Nature | null;
  openingDebit: number;
  openingCredit: number;
  debit: number;
  credit: number;
  closingDebit: number;
  closingCredit: number;
}

export interface TrialBalance {
  from: string;
  to: string;
  rows: TbRow[];
  openingDebit: number;
  openingCredit: number;
  debit: number;
  credit: number;
  closingDebit: number;
  closingCredit: number;
  balanced: boolean;
}

export interface Integrity {
  ok: boolean;
  unbalancedVouchers: string[];
  balanceMismatches: string[];
  lineMismatches: string[];
}

export interface CompanyOption {
  id: number;
  name: string;
  isActive?: boolean;
}

export interface Range {
  from: string;
  to: string;
}

// ── Calls ──────────────────────────────────────────────────────────────────

async function data<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  return (await p).data.data;
}

const base = (companyId: number) => `/api/accounting/${companyId}`;

export const companiesList = () => data<CompanyOption[]>(api.get('/api/companies'));

export const booksApi = {
  get: (c: number) => data<Books>(api.get(`${base(c)}/books`)),
  save: (c: number, body: { booksBeginDate: string | null; lockedUntil: string | null }) =>
    data<Books>(api.put(`${base(c)}/books`, body)),
  integrity: (c: number) => data<Integrity>(api.get(`${base(c)}/reports/integrity`)),
};

export const groupsApi = {
  list: (c: number) => data<Group[]>(api.get(`${base(c)}/groups`)),
  create: (c: number, body: GroupSave) => data<Group>(api.post(`${base(c)}/groups`, body)),
  update: (c: number, id: number, body: GroupSave) => data<Group>(api.put(`${base(c)}/groups/${id}`, body)),
  remove: (c: number, id: number) => data<null>(api.delete(`${base(c)}/groups/${id}`)),
};

export const ledgersApi = {
  list: (c: number, cashBank?: CashBank) =>
    data<Ledger[]>(api.get(`${base(c)}/ledgers`, { params: cashBank ? { cashBank } : undefined })),
  create: (c: number, body: LedgerSave) => data<Ledger>(api.post(`${base(c)}/ledgers`, body)),
  update: (c: number, id: number, body: LedgerSave) => data<Ledger>(api.put(`${base(c)}/ledgers/${id}`, body)),
  remove: (c: number, id: number) => data<null>(api.delete(`${base(c)}/ledgers/${id}`)),
};

export interface VoucherFilter extends Range {
  type?: VoucherType | '';
  status?: VoucherStatus | '';
  q?: string;
}

/** Drops empty filters so the server applies its own defaults. */
const clean = (p: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== ''));

export const vouchersApi = {
  list: (c: number, f: VoucherFilter) => data<VoucherRow[]>(api.get(`${base(c)}/vouchers`, { params: clean({ ...f }) })),
  get: (c: number, id: number) => data<Voucher>(api.get(`${base(c)}/vouchers/${id}`)),
  create: (c: number, body: VoucherIn) => api.post(`${base(c)}/vouchers`, body).then((r) => r.data as { message?: string; data: Voucher }),
  alter: (c: number, id: number, body: VoucherIn) =>
    api.put(`${base(c)}/vouchers/${id}`, body).then((r) => r.data as { message?: string; data: Voucher }),
  cancel: (c: number, id: number, reason: string, version: number) =>
    data<Voucher>(api.post(`${base(c)}/vouchers/${id}/cancel`, { reason, version })),
  history: (c: number, id: number) => data<AuditEntry[]>(api.get(`${base(c)}/vouchers/${id}/history`)),
};

export const reportsApi = {
  dayBook: (c: number, r: Range, type?: VoucherType | '') =>
    data<DayBook>(api.get(`${base(c)}/reports/day-book`, { params: clean({ ...r, type }) })),
  ledger: (c: number, ledgerId: number, r: Range) => data<LedgerStatement>(api.get(`${base(c)}/reports/ledger/${ledgerId}`, { params: r })),
  cashBank: (c: number, kind: CashBank, r: Range, ledgerId?: number | null) =>
    data<CashBankBook>(
      api.get(`${base(c)}/reports/${kind === 'CASH' ? 'cash-book' : 'bank-book'}`, { params: clean({ ...r, ledgerId }) })
    ),
  trialBalance: (c: number, r: Range, showZero: boolean) =>
    data<TrialBalance>(api.get(`${base(c)}/reports/trial-balance`, { params: { ...r, showZero } })),
};

/**
 * The voucher as a PDF for the in-page viewer. Fetched through the client (the endpoint needs
 * the bearer token); a JSON error arrives as a Blob, so it is read for the toast message.
 * The caller owns the object URL and must revoke it.
 */
export async function fetchVoucherPdf(companyId: number, id: number, voucherNo: string): Promise<QuotationPdfFile> {
  const res = await api
    .get<Blob>(`${base(companyId)}/vouchers/${id}/print.pdf`, { responseType: 'blob' })
    .catch(async (error: any) => {
      const body = error?.response?.data;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await body.text());
          error.userMessage = parsed?.message || parsed?.error || error.userMessage;
        } catch {
          // not JSON — keep the interceptor's message
        }
      }
      throw error;
    });
  return {
    url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })),
    filename: `${voucherNo.replace(/\//g, '-')}.pdf`,
  };
}

/**
 * The message to show for a failed call: the server's own (business rule, stale version, no
 * access), with the field problems of a validation error spelled out.
 */
export function errorText(e: any, fallback: string, code: string): string {
  const msg = logError({ message: fallback, error: e, code });
  const fields = e?.response?.data?.data;
  if (e?.response?.status === 422 && fields && typeof fields === 'object' && !Array.isArray(fields)) {
    const parts = Object.entries(fields as Record<string, unknown>).map(
      ([f, v]) => `${f}: ${Array.isArray(v) ? v.join(', ') : String(v)}`
    );
    if (parts.length) return `${msg} ${parts.join('; ')}`;
  }
  return msg;
}

export const isConflict = (e: any) => e?.response?.status === 409;

// ── Formatting & dates ─────────────────────────────────────────────────────

const NUM = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** 123456 → "1,23,456.00"; null → "". */
export const money = (v: number | string | null | undefined) =>
  v === null || v === undefined || v === '' ? '' : NUM.format(Number(v));
/** Like `money`, but blank for zero (statement and trial balance columns). */
export const amt = (v: number | null | undefined) => (v ? money(v) : '');
/** "1,23,456.00 Dr"; nil shows as "0.00". */
export const bal = (b: Balance | null | undefined) => (!b ? '' : Number(b.amount) === 0 ? '0.00' : `${money(b.amount)} ${b.side}`);

/** Rupees to paise, so totals add up exactly. */
export const paise = (v: number | string | null | undefined) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

export const localDate = (d: Date = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** 1 April of the financial year the date falls in. */
export const fyStart = (iso: string = localDate()) => {
  const [y, m] = iso.split('-').map(Number);
  return `${m >= 4 ? y : y - 1}-04-01`;
};

/** The current financial year to date. */
export const fyToDate = (): Range => ({ from: fyStart(), to: localDate() });

/** "2026-10-09" → "9 Oct 2026" */
export const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export const fmtDateTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const NATURES: Nature[] = ['ASSET', 'LIABILITY', 'INCOME', 'EXPENSE'];
export const natureLabel = (n: Nature | null | undefined) => (n ? n.charAt(0) + n.slice(1).toLowerCase() : '');
export const isRevenue = (n: Nature | null | undefined) => n === 'INCOME' || n === 'EXPENSE';

export interface TypeInfo {
  type: VoucherType;
  label: string;
  key: string;
  icon: string;
  color: 'error' | 'success' | 'info' | 'secondary';
}

/** Tally's function keys: F4 Contra, F5 Payment, F6 Receipt, F7 Journal. */
export const VOUCHER_TYPES: TypeInfo[] = [
  { type: 'PAYMENT', label: 'Payment', key: 'F5', icon: 'mdi:cash-minus', color: 'error' },
  { type: 'RECEIPT', label: 'Receipt', key: 'F6', icon: 'mdi:cash-plus', color: 'success' },
  { type: 'CONTRA', label: 'Contra', key: 'F4', icon: 'mdi:swap-horizontal', color: 'info' },
  { type: 'JOURNAL', label: 'Journal', key: 'F7', icon: 'mdi:book-open-variant', color: 'secondary' },
];
export const typeInfo = (t: VoucherType) => VOUCHER_TYPES.find((x) => x.type === t) ?? VOUCHER_TYPES[3];

/**
 * Which ledgers a voucher line may use (VoucherRules on the server):
 *   PAYMENT  credit cash / bank, debit anything else;  RECEIPT  the reverse;
 *   CONTRA   cash / bank only;                         JOURNAL  no cash / bank.
 */
export function ledgerAllowed(type: VoucherType, side: Side, l: Pick<Ledger, 'cashBank'>): boolean {
  const cb = l.cashBank !== null && l.cashBank !== undefined;
  switch (type) {
    case 'PAYMENT':
      return side === 'Cr' ? cb : !cb;
    case 'RECEIPT':
      return side === 'Dr' ? cb : !cb;
    case 'CONTRA':
      return cb;
    default:
      return !cb;
  }
}

/** "Primary › Current Assets › Bank Accounts" for a group, from the flat list. */
export function groupPath(groups: Group[], id: number | null | undefined): string {
  const byId = new Map(groups.map((g) => [g.id, g]));
  const names: string[] = [];
  let g = id ? byId.get(id) : undefined;
  let guard = 0;
  while (g && guard++ < 50) {
    names.unshift(g.name);
    g = g.parentId ? byId.get(g.parentId) : undefined;
  }
  return names.join(' › ');
}

/** The groups in tree order with their depth (children after their parent, by sort order then name). */
export function groupTree(groups: Group[]): { group: Group; depth: number }[] {
  const kids = new Map<number | null, Group[]>();
  groups.forEach((g) => {
    const k = g.parentId ?? null;
    kids.set(k, [...(kids.get(k) ?? []), g]);
  });
  kids.forEach((list) => list.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)));
  const out: { group: Group; depth: number }[] = [];
  const walk = (parent: number | null, depth: number) =>
    (kids.get(parent) ?? []).forEach((g) => {
      out.push({ group: g, depth });
      walk(g.id, depth + 1);
    });
  walk(null, 0);
  return out;
}
